/**
 * Sentinel Review Service
 * API calls for the Sentinel / Client-feedback review pages.
 */

import api from "@/lib/api";
import type { ApiResponse } from "@/lib/api";
import { withBuScope } from "@/lib/business-unit-scope";

/** Verdict on a Sentinel (Kim) response. */
export type SentinelVerdict = "yes" | "no";

/**
 * "What was wrong with it?" reason codes (only meaningful for a 'no' verdict) —
 * offered in the feedback panel. "other" pairs with a free-text note (otherText).
 */
export type SentinelReason =
  | "resolution"
  | "completeness"
  | "facts"
  | "brand_tone_style"
  | "sentiment"
  | "other";

/**
 * Codes retired when the panel moved to the dimensions above. Never offered for
 * new reviews, but still returned by the API for reviews captured under the old
 * wording, so every read path must be able to render them.
 */
export type SentinelLegacyReason = "tone_was_off" | "factually_incorrect" | "too_verbose" | "missed_the_point";

/** Any reason code a stored review may carry. */
export type SentinelReasonCode = SentinelReason | SentinelLegacyReason;

export interface SentinelConversationListItem {
  conversationId: number;
  externalId: string | null;
  displayId: string | null;
  subject: string | null;
  channel: string | null;
  categories: string[] | null;
  contact: { name: string | null; email: string | null } | null;
  pendingCount: number;
  reviewedCount: number;
  lastSentinelAt: string | null;
  preview: string | null;
}

export interface SentinelConversationsResponse {
  conversations: SentinelConversationListItem[];
  nextCursor: number | null;
  hasMore: boolean;
  totalCount: number;
}

export interface SentinelMessage {
  id: number;
  content: string | null;
  messageType: number;
  senderType: string | null;
  isAiAssistUsed: boolean;
  createdAt: string;
}

/** A submitted review attached to a thread turn. */
export interface SentinelReviewSummary {
  id: string;
  verdict: SentinelVerdict;
  reasons: SentinelReasonCode[];
  otherText: string | null;
  rewrite: string | null;
  reviewer: { id: string; email: string | null };
  addressed: boolean;
  createdAt: string;
}

/** One turn of the full conversation thread. */
export interface SentinelThreadTurn {
  customerMessage: SentinelMessage;
  /**
   * Kim's response for this customer message, or null when Kim never drafted
   * one. Null turns are still returned so the thread shows the whole
   * conversation, not only the exchanges that were sent to review.
   */
  sentinelMessage: SentinelMessage | null;
  /** Human agent's actual reply — shown for comparison on earlier turns. */
  agentMessage: SentinelMessage | null;
  /** The latest (current) turn shows Kim only; earlier turns show the agent reply too. */
  isLatest: boolean;
  /** Existing review for this Sentinel response, if any (editable). */
  review: SentinelReviewSummary | null;
}

export interface SentinelConversationHeader {
  conversationId: number;
  externalId: string | null;
  displayId: string | null;
  subject: string | null;
  channel: string | null;
  contact: { name: string | null; email: string | null } | null;
}

export interface SentinelConversationThread {
  conversation: SentinelConversationHeader;
  turns: SentinelThreadTurn[];
}

export interface SentinelListFilters {
  contactSearch?: string;
  startDate?: Date;
  endDate?: Date;
  sortOrder?: "asc" | "desc";
  reviewStatus?: "reviewed" | "not_reviewed";
}

export interface SubmitSentinelReviewInput {
  conversationId: number;
  sentinelMessageId: number;
  verdict: SentinelVerdict;
  reasons?: SentinelReason[];
  otherText?: string;
  rewrite?: string;
}

/** Fields a reviewer can change on an existing review (PATCH). */
export interface EditSentinelReviewInput {
  verdict?: SentinelVerdict;
  reasons?: SentinelReason[];
  otherText?: string | null;
  rewrite?: string | null;
}

export interface ReviewMessageSnapshot {
  id: number;
  content: string | null;
}

export interface SentinelReviewItem {
  id: string;
  createdAt: string;
  reviewer: { id: string; email: string | null };
  verdict: SentinelVerdict;
  reasons: SentinelReasonCode[];
  otherText: string | null;
  rewrite: string | null;
  addressed: boolean;
  conversationId: string;
  externalId: string | null;
  customerMessage: ReviewMessageSnapshot | null;
  sentinelMessage: ReviewMessageSnapshot | null;
  agentMessage: ReviewMessageSnapshot | null;
}

export interface SentinelReviewsResponse {
  items: SentinelReviewItem[];
  total: number;
  page: number;
  limit: number;
}

export interface SentinelReviewsFilters {
  verdict?: SentinelVerdict;
  page?: number;
  limit?: number;
}

function toLocalDateParam(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export const sentinelReviewService = {
  /**
   * List conversations that have at least one Sentinel response.
   */
  async getSentinelConversations(
    limit: number = 20,
    cursor?: number,
    filters?: SentinelListFilters,
    scope?: { bu_id?: string },
  ): Promise<SentinelConversationsResponse> {
    const params = new URLSearchParams();
    params.append("limit", limit.toString());
    if (cursor) params.append("cursor", cursor.toString());
    if (filters?.startDate) params.append("startDate", toLocalDateParam(filters.startDate));
    if (filters?.endDate) params.append("endDate", toLocalDateParam(filters.endDate));
    if (filters?.contactSearch) params.append("contactSearch", filters.contactSearch);
    if (filters?.sortOrder) params.append("sortOrder", filters.sortOrder);
    if (filters?.reviewStatus) params.append("reviewStatus", filters.reviewStatus);
    if (scope?.bu_id) params.append("bu_id", scope.bu_id);

    const response = await api.get<ApiResponse<SentinelConversationsResponse>>(
      `/sentinel/conversations?${params.toString()}`,
    );
    return response.data.data;
  },

  /**
   * Get a conversation's full thread (all turns + attached reviews).
   */
  async getConversationThread(conversationId: number): Promise<SentinelConversationThread> {
    const response = await api.get<ApiResponse<SentinelConversationThread>>(
      `/sentinel/conversations/${conversationId}`,
    );
    return response.data.data;
  },

  /**
   * Administrator: rewrite a Sentinel response's markdown body in place.
   */
  async updateResponse(sentinelMessageId: number, content: string): Promise<SentinelMessage> {
    const response = await api.patch<ApiResponse<SentinelMessage>>(`/sentinel/responses/${sentinelMessageId}`, {
      content,
    });
    return response.data.data;
  },

  /**
   * Administrator: delete a single Sentinel response (its review goes with it).
   */
  async deleteResponse(sentinelMessageId: number): Promise<void> {
    await api.delete<ApiResponse<null>>(`/sentinel/responses/${sentinelMessageId}`);
  },

  /**
   * Administrator: delete every Sentinel response in a conversation, which
   * removes it from the review dashboard list.
   */
  async deleteConversationResponses(conversationId: number): Promise<{ deleted: number }> {
    const response = await api.delete<ApiResponse<{ deleted: number }>>(
      `/sentinel/conversations/${conversationId}/responses`,
    );
    return response.data.data;
  },

  /**
   * Create or update the review for a single Sentinel response.
   */
  async submitReview(input: SubmitSentinelReviewInput): Promise<unknown> {
    const response = await api.post<ApiResponse<unknown>>(`/sentinel/reviews`, input);
    return response.data.data;
  },

  /**
   * Edit an existing review.
   */
  async editReview(reviewId: string, patch: EditSentinelReviewInput): Promise<unknown> {
    const response = await api.patch<ApiResponse<unknown>>(`/sentinel/reviews/${reviewId}`, patch);
    return response.data.data;
  },

  /**
   * Delete a review (re-opens the response for review).
   */
  async deleteReview(reviewId: string): Promise<void> {
    await api.delete<ApiResponse<null>>(`/sentinel/reviews/${reviewId}`);
  },

  /**
   * Mark a review addressed / not addressed (Client Feedback view).
   */
  async setAddressed(reviewId: string, addressed: boolean): Promise<unknown> {
    const response = await api.patch<ApiResponse<unknown>>(`/sentinel/reviews/${reviewId}/addressed`, { addressed });
    return response.data.data;
  },

  /**
   * Email a CSV export of reviews (optionally date-filtered) to an address.
   */
  async exportReviews(
    input: { email: string; startDate?: string; endDate?: string },
    scope?: { bu_id?: string },
  ): Promise<{ count: number }> {
    // The brand rides on the URL even though this is a POST: the body is the export's own
    // parameters, and every scoped write in this app spells the scope the same way.
    const response = await api.post<ApiResponse<{ count: number }>>(
      withBuScope(`/sentinel/reviews/export`, scope ?? {}),
      input,
    );
    return response.data.data;
  },

  /**
   * List submitted reviews for the account (feedback-viewer page).
   */
  async getReviews(filters?: SentinelReviewsFilters, scope?: { bu_id?: string }): Promise<SentinelReviewsResponse> {
    const params = new URLSearchParams();
    if (filters?.verdict) params.append("verdict", filters.verdict);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (scope?.bu_id) params.append("bu_id", scope.bu_id);

    const response = await api.get<ApiResponse<SentinelReviewsResponse>>(
      `/sentinel/reviews?${params.toString()}`,
    );
    return response.data.data;
  },
};
